import { DeviceTrendPoints, DisplayRef } from '@repo/sdk/models/components';

import {
  SelectionsDeviceGroup,
  SidebarAccordionItem,
} from '@/features/trends/components/selections-sidebar/selections.types';

/**
 * Extended DeviceTrendPoints type that can include dynamically filtered displayRefs.
 * Used internally when search filtering narrows down which refs to show.
 */
export type DeviceTrendPointsWithFilteredRefs = DeviceTrendPoints & {
  /**
   * Optional filtered display refs when only some refs match the search.
   * If present, these should be used instead of allDisplayRefs/templateDisplayRefs.
   */
  displayRefs?: DisplayRef[];
};

/**
 * Supported value kinds for Trends feature.
 * Only Number, Boolean, and Enum are currently supported for plotting.
 */
const SUPPORTED_VALUE_KINDS = new Set(['number', 'boolean', 'enum']);

/**
 * Filters displayRefs to only include supported value kinds.
 * Excludes Schedule, Sequence, and String types which cannot be plotted.
 */
export function filterSupportedValueKinds(displayRefs: DisplayRef[] | undefined): DisplayRef[] {
  if (!displayRefs) return [];
  return displayRefs.filter((ref) => SUPPORTED_VALUE_KINDS.has(ref.ref.valueKind));
}

/**
 * Filters and maps devices based on search term.
 * Returns null for devices that don't match the search criteria.
 * May attach a filtered `displayRefs` property when only some refs match.
 * Always filters out unsupported value kinds (Schedule, Sequence, String).
 */
export function filterDeviceBySearchTerm(
  device: DeviceTrendPoints,
  searchTerm: string,
  showAdvanced: boolean
): DeviceTrendPointsWithFilteredRefs | null {
  if (!searchTerm) {
    return device;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();
  const displayRefs = showAdvanced ? device.allDisplayRefs : device.defaultDisplayRefs;
  // Filter for supported value kinds first
  const supportedRefs = filterSupportedValueKinds(displayRefs);

  const deviceMatches = device.displayName.toLowerCase().includes(lowerSearchTerm);
  const hasMatchingRefs = supportedRefs.some((ref) =>
    ref.label.toLowerCase().includes(lowerSearchTerm)
  );

  // Device doesn't match at all
  if (!deviceMatches && !hasMatchingRefs) {
    return null;
  }

  // Device name matches - return full device
  if (deviceMatches) {
    return device;
  }

  // Only refs match - return device with filtered refs
  const filteredRefs = supportedRefs.filter((ref) =>
    ref.label.toLowerCase().includes(lowerSearchTerm)
  );

  return {
    ...device,
    displayRefs: filteredRefs,
  };
}

/**
 * Groups devices by their kind using a Map for efficient grouping.
 */
export function groupDevicesByKind(
  devices: DeviceTrendPointsWithFilteredRefs[]
): Map<string, DeviceTrendPointsWithFilteredRefs[]> {
  return devices.reduce((acc, device) => {
    const kind = device.kind || 'Unknown';
    if (!acc.has(kind)) {
      acc.set(kind, []);
    }
    acc.get(kind)!.push(device);
    return acc;
  }, new Map<string, DeviceTrendPointsWithFilteredRefs[]>());
}

/**
 * Gets display refs for a device based on advanced mode setting.
 * If the device has filtered displayRefs (from search), uses those instead.
 * Always filters out unsupported value kinds (Schedule, Sequence, String).
 */
export function getDisplayRefs(device: DeviceTrendPointsWithFilteredRefs, showAdvanced: boolean) {
  // If search filtering has already narrowed displayRefs, use those
  if (device.displayRefs) {
    return filterSupportedValueKinds(device.displayRefs);
  }
  // Otherwise use full list based on advanced mode
  const refs = showAdvanced ? device.allDisplayRefs : device.defaultDisplayRefs;
  return filterSupportedValueKinds(refs);
}

/**
 * Transforms a device into a sidebar accordion item with proper formatting.
 */
export function transformDeviceToAccordionItem(
  device: DeviceTrendPointsWithFilteredRefs,
  showAdvanced: boolean
) {
  const displayRefs = getDisplayRefs(device, showAdvanced);

  return {
    value: device.deviceId,
    title: `${device.displayName}`,
    data: {
      alias: device.alias,
      deviceId: device.deviceId,
      title: device.displayName,
      displayName: device.displayName,
      displayRefs: displayRefs.sort((a, b) => a.label.localeCompare(b.label)),
    },
  };
}

/**
 * Transforms grouped devices map into accordion items structure with sorting.
 */
export function transformGroupedDevicesToAccordion(
  groupedDevices: Map<string, DeviceTrendPointsWithFilteredRefs[]>,
  showAdvanced: boolean
): SidebarAccordionItem<SelectionsDeviceGroup>[] {
  return Array.from(groupedDevices.entries())
    .map(([kind, devices]) => ({
      value: kind,
      title: kind,
      data: {
        kind,
        devices: devices
          .map((device) => transformDeviceToAccordionItem(device, showAdvanced))
          .sort((a, b) => a.data.displayName.localeCompare(b.data.displayName)),
      },
    }))
    .sort((a, b) => a.title.localeCompare(b.title));
}
